import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Status.css';

function Status() {
  const [serverStatus, setServerStatus] = useState({
    isRunning: false,
    uptime: 0,
    connectedClients: 0,
    lastChecked: null
  });
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('lastHour'); // 'lastHour', 'lastDay', 'lastWeek'

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Detect environment
        const isLocalhost = window.location.hostname === 'localhost' ||
                            window.location.hostname === '127.0.0.1' ||
                            window.location.hostname.startsWith('192.168.');

        const baseUrl = isLocalhost ? 'http://localhost:5000' : '';

        // Fetch both status and statistics
        const [statusResponse, statsResponse] = await Promise.all([
          fetch(`${baseUrl}/status`),
          fetch(`${baseUrl}/statistics`)
        ]);

        const statusData = await statusResponse.json();
        const statsData = await statsResponse.json();

        setServerStatus({
          isRunning: true,
          uptime: statusData.uptime,
          connectedClients: statusData.connectedClients,
          lastChecked: new Date()
        });

        setStatistics(statsData);
      } catch (error) {
        console.error('Failed to fetch server data:', error);
        setServerStatus({
          isRunning: false,
          uptime: 0,
          connectedClients: 0,
          lastChecked: new Date()
        });
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchData();

    // Refresh every 5 seconds
    const interval = setInterval(fetchData, 5000);

    return () => clearInterval(interval);
  }, []);

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    parts.push(`${secs}s`);

    return parts.join(' ');
  };

  const renderBarChart = (data, title) => {
    const maxValue = Math.max(...Object.values(data), 1);
    const roleColors = {
      'Speaker': '#000000',
      'Clerk': '#0000FF',
      'Deputy Clerk': '#008000',
      'Unknown': '#FF0000'
    };

    return (
      <div className="chart-container">
        <h3>{title}</h3>
        <div className="bar-chart">
          {Object.entries(data).map(([role, count]) => (
            <div key={role} className="bar-row">
              <div className="bar-label">{role}</div>
              <div className="bar-wrapper">
                <div
                  className="bar-fill"
                  style={{
                    width: `${(count / maxValue) * 100}%`,
                    backgroundColor: roleColors[role]
                  }}
                />
                <span className="bar-value">{count}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTimelineChart = (messages, timeRange) => {
    if (!messages || messages.length === 0) {
      return <div className="chart-empty">No messages in this time period</div>;
    }

    // Group messages by time buckets
    const now = Date.now();
    let bucketSize, bucketCount;

    if (timeRange === 'lastHour') {
      bucketSize = 5 * 60 * 1000; // 5 minutes
      bucketCount = 12;
    } else if (timeRange === 'lastDay') {
      bucketSize = 60 * 60 * 1000; // 1 hour
      bucketCount = 24;
    } else {
      bucketSize = 24 * 60 * 60 * 1000; // 1 day
      bucketCount = 7;
    }

    const buckets = Array(bucketCount).fill(0);
    const startTime = now - (bucketSize * bucketCount);

    messages.forEach(msg => {
      const msgTime = new Date(msg.timestamp).getTime();
      if (msgTime > startTime) {
        const bucketIndex = Math.floor((msgTime - startTime) / bucketSize);
        if (bucketIndex >= 0 && bucketIndex < bucketCount) {
          buckets[bucketIndex]++;
        }
      }
    });

    const maxValue = Math.max(...buckets, 1);

    // Reverse buckets so most recent appears on the left
    const reversedBuckets = [...buckets].reverse();

    return (
      <div className="timeline-chart">
        {reversedBuckets.map((count, index) => {
          // Calculate the actual time/date for this bucket
          // Note: reversedBuckets are reversed, so index 0 is most recent
          const bucketOriginalIndex = bucketCount - 1 - index;
          const bucketTime = new Date(startTime + (bucketOriginalIndex * bucketSize) + (bucketSize / 2));

          let label = '';
          if (timeRange === 'lastHour') {
            label = `${index * 5}m`;
          } else if (timeRange === 'lastDay') {
            // Show actual hour (0-23)
            label = bucketTime.getHours().toString();
          } else if (timeRange === 'lastWeek') {
            // Show date as DD/MM
            const day = bucketTime.getDate();
            const month = bucketTime.getMonth() + 1; // getMonth() is 0-indexed
            label = `${day}/${month}`;
          }

          return (
            <div key={index} className="timeline-bar">
              <div
                className="timeline-fill"
                style={{
                  height: `${(count / maxValue) * 100}%`,
                  backgroundColor: '#3182ce'
                }}
                title={`${count} messages`}
              />
              <div className="timeline-label">
                {label}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const getCurrentStats = () => {
    if (!statistics) return null;
    return statistics[timeRange] || {};
  };

  return (
    <div className="status-page-wrapper">
      <div className="status-container">
        <div className="status-header">
          <h1>Server Status & Statistics</h1>
          <Link to="/" className="back-link">← Back to Chat</Link>
        </div>

        {loading ? (
          <div className="status-loading">Loading server data...</div>
        ) : (
          <>
            {/* Server Status Cards */}
            <div className="status-grid">
              <div className="status-card">
                <div className="status-label">Server Status</div>
                <div className={`status-value ${serverStatus.isRunning ? 'status-online' : 'status-offline'}`}>
                  {serverStatus.isRunning ? '● Online' : '● Offline'}
                </div>
              </div>

              <div className="status-card">
                <div className="status-label">Uptime</div>
                <div className="status-value">
                  {serverStatus.isRunning ? formatUptime(serverStatus.uptime) : 'N/A'}
                </div>
              </div>

              <div className="status-card">
                <div className="status-label">Connected Clients</div>
                <div className="status-value">
                  {serverStatus.connectedClients}
                </div>
              </div>

              <div className="status-card">
                <div className="status-label">Last Updated</div>
                <div className="status-value status-small">
                  {serverStatus.lastChecked ? serverStatus.lastChecked.toLocaleTimeString() : 'N/A'}
                </div>
              </div>
            </div>

            {/* Statistics Section */}
            {statistics && (
              <>
                {/* Time Range Selector */}
                <div className="time-range-selector">
                  <button
                    className={timeRange === 'lastHour' ? 'active' : ''}
                    onClick={() => setTimeRange('lastHour')}
                  >
                    Last Hour
                  </button>
                  <button
                    className={timeRange === 'lastDay' ? 'active' : ''}
                    onClick={() => setTimeRange('lastDay')}
                  >
                    Last 24 Hours
                  </button>
                  <button
                    className={timeRange === 'lastWeek' ? 'active' : ''}
                    onClick={() => setTimeRange('lastWeek')}
                  >
                    Last 7 Days
                  </button>
                </div>

                {/* Key Metrics */}
                <div className="metrics-grid">
                  <div className="metric-card">
                    <div className="metric-value">{getCurrentStats().messageCount || 0}</div>
                    <div className="metric-label">Messages</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{getCurrentStats().connections || 0}</div>
                    <div className="metric-label">Connections</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{getCurrentStats().disconnections || 0}</div>
                    <div className="metric-label">Disconnections</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{getCurrentStats().avgMessageLength || 0}</div>
                    <div className="metric-label">Avg Length</div>
                  </div>
                </div>

                {/* Message Timeline Chart */}
                <div className="chart-section">
                  <h2>Message Activity Over Time</h2>
                  {renderTimelineChart(
                    statistics.timeline.messages.filter(msg => {
                      const msgTime = new Date(msg.timestamp).getTime();
                      const now = Date.now();
                      if (timeRange === 'lastHour') return msgTime > now - 60 * 60 * 1000;
                      if (timeRange === 'lastDay') return msgTime > now - 24 * 60 * 60 * 1000;
                      return msgTime > now - 7 * 24 * 60 * 60 * 1000;
                    }),
                    timeRange
                  )}
                </div>

                {/* Messages by Role Chart */}
                <div className="chart-section">
                  {renderBarChart(
                    getCurrentStats().messagesByRole || {},
                    'Messages by Role'
                  )}
                </div>

                {/* All-Time Statistics */}
                <div className="alltime-section">
                  <h2>All-Time Statistics</h2>
                  <div className="alltime-grid">
                    <div className="alltime-card">
                      <div className="alltime-label">Total Messages</div>
                      <div className="alltime-value">{statistics.totalMessages}</div>
                    </div>
                    <div className="alltime-card">
                      <div className="alltime-label">Total Connections</div>
                      <div className="alltime-value">{statistics.totalConnections}</div>
                    </div>
                    <div className="alltime-card">
                      <div className="alltime-label">Server Started</div>
                      <div className="alltime-value alltime-small">
                        {new Date(statistics.serverStartTime).toLocaleString()}
                      </div>
                    </div>
                    <div className="alltime-card">
                      <div className="alltime-label">Avg Message Length</div>
                      <div className="alltime-value">{statistics.allTime.avgMessageLength}</div>
                    </div>
                  </div>

                  {/* All-time messages by role */}
                  {renderBarChart(
                    statistics.allTime.messagesByRole,
                    'All-Time Messages by Role'
                  )}
                </div>
              </>
            )}
          </>
        )}

        <div className="status-info">
          All statistics update every 5 seconds. No message content is stored.
        </div>
      </div>
    </div>
  );
}

export default Status;
